import { useCallback, useContext } from "react";

import { BrazeOperatorContext } from "@lib/BrazeOperatorContext";
import type { AgenticAiRegisterTool, OperatorDisabledProps } from "@lib/agenticAi/types";

/**
 * Hook to get the registerChangeTool function from BrazeOperatorContext.
 *
 * This hook provides a wrapper around the base registerTool that automatically
 * checks if agentic changes are enabled before registering the tool.
 * If not within a BrazeOperatorProvider or if agentic changes are disabled,
 * the tool will not be registered and a no-op cleanup function will be returned.
 *
 * @example
 * const MyComponent = () => {
 *   const registerChangeTool = useRegisterChangeTool();
 *
 *   useEffect(() => {
 *     const cleanup = registerChangeTool({
 *       name: "my_tool",
 *       description: "My tool description",
 *       parameters: z.object({}),
 *       callback: () => "done",
 *     });
 *
 *     return cleanup;
 *   }, [registerChangeTool]);
 * };
 */
export const useRegisterChangeTool = ({
  operatorDisabledReason,
}: OperatorDisabledProps = {}): AgenticAiRegisterTool => {
  const { registerChangeTool } = useContext(BrazeOperatorContext);
  const disabledRegisterChangeTool = useCallback<AgenticAiRegisterTool>(() => () => {}, []);

  return operatorDisabledReason === undefined ? registerChangeTool : disabledRegisterChangeTool;
};
